import styled, { keyframes } from 'styled-components';

const INK = '#1d2340';
const PAPER = '#f8f5ee';
const LINE = '#e7e1d3';
const PEN = '#d9412b';
const MUTED = '#6f6a5c';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const AuthContainer = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${PAPER};
  padding: 48px 20px 64px;
  position: relative;
`;

export const AuthCard = styled.div`
  max-width: ${(props) => props.maxWidth || '420px'};
  width: 100%;
  background: #fff;
  border: 1px solid ${LINE};
  border-radius: 4px;
  padding: ${(props) => props.padding || '36px 32px 28px'};
  box-shadow: 6px 6px 0 #e9e2d0;
  animation: ${fadeIn} 0.35s ease-out;
  position: relative;

  @media (max-width: 480px) {
    padding: 28px 20px 22px;
    box-shadow: none;
  }
`;

export const LogoSection = styled.div`
  margin-bottom: 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

export const LogoIcon = styled.div``;

export const AuthTitle = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: ${INK};
`;

export const AuthSubtitle = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.6;
  letter-spacing: -0.02em;
  color: ${MUTED};
  text-align: center;
`;

export const FormSection = styled.form`
  margin-bottom: 8px;
`;

export const StyledTextField = styled.div`
  position: relative;
  margin-bottom: 16px;

  label {
    display: block;
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: ${INK};
    text-align: left;
  }

  input {
    width: 100%;
    padding: 13px 14px;
    border: 1px solid ${LINE};
    border-radius: 6px;
    font-family: inherit;
    font-size: 15px;
    letter-spacing: -0.02em;
    line-height: 1.4;
    background: #fff;
    color: ${INK};
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus {
      outline: none;
      border-color: ${INK};
      box-shadow: 0 0 0 3px rgba(29, 35, 64, 0.08);
    }

    &:disabled {
      background: ${PAPER};
      color: ${MUTED};
      cursor: not-allowed;
    }

    &::placeholder {
      color: #b3ac9c;
    }
  }

  .error-message {
    margin-top: 6px;
    font-size: 12px;
    line-height: 1.45;
    text-align: left;
    color: ${PEN};
  }
`;

export const PasswordField = styled(StyledTextField)`
  .password-toggle {
    position: absolute;
    right: 6px;
    top: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    color: ${MUTED};
    background: none;
    border: none;
    border-radius: 6px;
    cursor: pointer;

    &:hover {
      color: ${INK};
      background: ${PAPER};
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  input {
    padding-right: 44px;
  }
`;

export const ButtonSection = styled.div`
  margin-bottom: 12px;
`;

export const ModernButton = styled.button`
  width: 100%;
  min-height: 48px;
  padding: 13px 18px;
  border-radius: 6px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.02em;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  ${(props) =>
    props.variant === 'primary'
      ? `
    background: ${INK};
    color: #fff;
    border: 1px solid ${INK};

    &:hover:not(:disabled) {
      background: #2c3561;
    }
  `
      : `
    background: #fff;
    color: ${INK};
    border: 1px solid ${LINE};

    &:hover:not(:disabled) {
      background: ${PAPER};
      border-color: ${INK};
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const Alert = styled.div`
  padding: 12px 14px;
  margin-bottom: 16px;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.55;
  text-align: left;

  ${(props) => {
    if (props.severity === 'error') {
      return `
        background: #fff4f1;
        color: #9d2d1d;
        border: 1px solid #f0c8bf;
      `;
    }
    if (props.severity === 'success') {
      return `
        background: #f4f7ef;
        color: #2f4a28;
        border: 1px solid #d5e0c8;
      `;
    }
    return `
      background: ${PAPER};
      color: ${INK};
      border: 1px solid ${LINE};
    `;
  }}
`;

export const ToggleSection = styled.div`
  margin-top: 18px;
  text-align: center;
  font-size: 14px;
  letter-spacing: -0.02em;
  color: ${MUTED};
`;

export const ToggleButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  font-size: inherit;
  font-weight: 400;
  letter-spacing: inherit;
  cursor: pointer;

  strong {
    margin-left: 4px;
    font-weight: 700;
    color: ${INK};
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  &:hover strong {
    color: ${PEN};
  }
`;

export const Footer = styled.div`
  display: none;
`;

export const FloatingElements = styled.div`
  display: none;
`;

export const PasswordToggle = ({ visible, ...props }) => (
  <button
    type="button"
    className="password-toggle"
    aria-label={visible ? '비밀번호 숨기기' : '비밀번호 보기'}
    {...props}
  >
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M2.5 12s3.5-6.5 9.5-6.5S21.5 12 21.5 12s-3.5 6.5-9.5 6.5S2.5 12 2.5 12Z"
        stroke="currentColor"
        strokeWidth="1.7"
      />
      <circle cx="12" cy="12" r="2.6" stroke="currentColor" strokeWidth="1.7" />
      {visible && <path d="M4 19 20 5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />}
    </svg>
  </button>
);
